
import { Options, Vue } from 'vue-class-component';
import VoucherService from "@/services/VoucherService";
import Alert from "@/components/Alert.vue";
import { VoucherRedemptionCheckResult, VoucherType } from "@/data/voucher";
import GlobalStore from "@/utils/global-store";
import EventBusService from "@/services/EventBusService";
import { RecordIdentifier } from "@/data/global";

type VoucherCheckContext = 'checkout' | 'free';

type VoucherCheckState = 'valid' | 'invalid' | 'contextMismatch' | 'rateLimited' | 'error' | null;

@Options({
    props: {
        context: {
            type: String,
            required: false,
            default: 'free',
        },
        regionId: {
            type: Number,
            required: false,
            default: null,
        },
        buttonLabel: {
            type: String,
            required: false,
            default: 'Einlösen',
        },
    },
    components: {
        Alert,
    },
    emits: [
        'valid',
        'invalid',
        'reset',
    ],
})
export default class VoucherCheck extends Vue {
    context!: VoucherCheckContext

    regionId!: RecordIdentifier|null

    voucherCodeInputValue = ''
    state: VoucherCheckState = null
    activeVoucherInfo: string|null = null
    locked = false
    loading = false

    get stateAlertType() {
        if( this.state === null ) {
            return 'info';
        }
        return this.isValid ? 'success' : 'danger';
    }

    get isValid() {
        return this.state === 'valid';
    }
    get isInvalid() {
        if( this.state === null ) {
            return false;
        }
        return !this.isValid;
    }

    onSubmit() {
        if( this.loading ) {
            return;
        }

        if( this.locked ) {
            this.voucherCodeInputValue = '';
            this.storeToLocalStorage('');
            this.locked = false;
            this.activeVoucherInfo = null;
            this.$emit('reset');
            this.$nextTick(() => {
                (this.$refs.voucherCode as any).focus();
            });
        }
        else {
            this.check();
            this.$nextTick(() => {
                (this.$refs.submit as any).blur();
            });
        }
    }

    check() {
        if( !this.voucherCodeInputValue ) {
            return;
        }
        const voucherCode = this.voucherCodeInputValue;
        this.loading = true;
        VoucherService.checkVoucher(voucherCode, this.regionId).then(result => {
            const voucher = result.data?.voucher as VoucherRedemptionCheckResult|undefined;
            if( this.context ) {
                if( this.context === 'checkout' ) {
                    // in context "checkout" we can only handle discount vouchers
                    if( voucher?.type !== VoucherType.Discount ) {
                        this.state = 'contextMismatch';
                        this.$emit('invalid');
                        return;
                    }

                    if( voucher?.discount_percentage ) {
                        this.activeVoucherInfo = voucher?.discount_percentage + '% Rabatt';
                    }
                }
                if( this.context === 'free' ) {
                    if( voucher?.type === VoucherType.Discount ) {
                        this.state = 'contextMismatch';
                        this.$emit('invalid');
                        return;
                    }
                }
            }

            // console.log('valid voucher', voucher);
            // result.data.redeemable should always be true here
            this.state = 'valid';
            this.locked = true;
            this.storeToLocalStorage(voucherCode);
            this.$emit('valid', voucher);
        }).catch(error => {
            if( error.response.status === 429 ) { // Too Many Requests (rate limited)
                EventBusService.$notification({
                    type: 'error',
                    message: 'Zu viele Anfragen. Bitte versuche es in wenigen Sekunden noch einmal.',
                });
                return;
            }

            // console.error('invalid voucher', error);
            this.state = 'invalid';
            this.$emit('invalid');
            if( error.response.status === 500 ) {
                this.state = 'error';
            }
        }).finally(() => {
            this.loading = false;
        });
    }

    loadFromLocalStorage() {
        const voucherCode = GlobalStore.data.voucherCode;

        if( !voucherCode.value || this.voucherCodeInputValue ) {
            return;
        }

        this.voucherCodeInputValue = voucherCode.value || '';
        this.locked = true;
    }

    storeToLocalStorage(code: string) {
        GlobalStore.set('voucherCode', code.toUpperCase());
    }

    mounted() {
        this.$watch('voucherCodeInputValue', () => {
            this.state = null;
        });

        this.loadFromLocalStorage();
        this.check();
    }
}
